'use client';

import Loader from '@/components/ui/loader';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { PhoneInput } from '@/components/ui/phone-input';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@heidi/design-system/form';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { Textarea } from '@heidi/design-system/textarea';
import {
  TypographyCaption,
  TypographyP,
  typographyVariant,
} from '@heidi/design-system/typography';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtom, useSetAtom } from 'jotai';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { type Country } from 'react-phone-number-input';

import {
  createTrialUpgradeRequestCallbackModalSchema,
  TRIAL_UPGRADE_REQUEST_CALLBACK_MESSAGE_MAX_LENGTH,
  trialUpgradeRequestCallbackModalMessages,
  type TrialUpgradeRequestCallbackModalFormValues,
} from '@heidi/commerce';
import { useUserTier } from '@heidi/commerce/data';
import { countries } from '@heidi/utils/countries';
import {
  closeTrialUpgradeRequestCallbackModalAtom,
  trialUpgradeRequestCallbackModalOpenAtom,
} from './atoms';
import { useTrialUpgradeRequestCallbackForm } from './useTrialUpgradeRequestCallbackForm';
import { useTrialUpgradeRequestCallbackModal } from './useTrialUpgradeRequestCallbackModal';

/**
 * Lets a user request a call back from a Heidi expert about upgrading to
 * Clinician or Practice (copy and styling follow the user's current tier).
 * Collects a short message and contact phone number, then submits the request
 * via HubSpot.
 *
 * Self-contained: open state is owned by
 * {@link trialUpgradeRequestCallbackModalOpenAtom}. Callers open via
 * {@link openTrialUpgradeRequestCallbackModalAtom} and the modal is mounted
 * once in `GlobalModalsWrapper`.
 *
 * Figma: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10138-52789
 */
export function TrialUpgradeRequestCallbackModal() {
  const intl = useIntl();
  const { data: user } = useQueryUser();
  const { userTier } = useUserTier();
  const { onSubmit, isSubmitting, hasCallbackRequested } =
    useTrialUpgradeRequestCallbackForm();
  const [open, setOpen] = useAtom(trialUpgradeRequestCallbackModalOpenAtom);
  const closeModal = useSetAtom(closeTrialUpgradeRequestCallbackModalAtom);

  const defaultCountry =
    countries.find((c) => c.name === user?.country)?.countryCode ?? 'AU';

  const isClinicianPlan =
    userTier === SubscriptionTier.CLINICIAN_PRO || userTier === SubscriptionTier.PRO;

  const schema = useMemo(
    () => createTrialUpgradeRequestCallbackModalSchema(intl.formatMessage),
    [intl.formatMessage]
  );

  const form = useForm<TrialUpgradeRequestCallbackModalFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      message: '',
      phoneNumber: '',
    },
  });
  const { reset } = form;

  useEffect(() => {
    if (!open) return;
    reset({
      message: '',
      phoneNumber: user?.phone_number ?? '',
    });
  }, [open, reset, user?.phone_number]);

  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if (!next && isSubmitting) return;
      setOpen(next);
    },
    [isSubmitting, setOpen]
  );

  const { handleBack, handleCancel, handleSubmit } = useTrialUpgradeRequestCallbackModal({
    open,
    close: () => handleDialogOpenChange(false),
    closeModal,
    onSubmitForm: onSubmit,
  });

  const descriptionMessage = isClinicianPlan
    ? trialUpgradeRequestCallbackModalMessages.descriptionClinician
    : trialUpgradeRequestCallbackModalMessages.descriptionPractice;
  const messagePlaceholder = intl.formatMessage(
    isClinicianPlan
      ? trialUpgradeRequestCallbackModalMessages.messagePlaceholderClinician
      : trialUpgradeRequestCallbackModalMessages.messagePlaceholderPractice
  );
  const contactNumberPlaceholder = intl.formatMessage(
    trialUpgradeRequestCallbackModalMessages.contactNumberPlaceholder
  );
  const backAriaLabel = intl.formatMessage(trialUpgradeRequestCallbackModalMessages.back);

  return (
    <DialogDrawer open={open} onOpenChange={handleDialogOpenChange}>
      <DialogDrawerContent
        overlayClassName="z-[100]"
        className="z-[101] flex w-full max-w-[470px] flex-col gap-0 border-border bg-surface p-0 pb-8 shadow-lg sm:rounded-2xl"
        data-testid="trial-upgrade-request-callback-modal"
      >
        <Button
          type="button"
          variant="ghost"
          size="md"
          iconOnly
          aria-label={backAriaLabel}
          onClick={handleBack}
          disabled={isSubmitting}
          className="absolute start-1.5 top-1.5 z-10 shrink-0 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
        >
          <ArrowLeft className="size-4 rtl:-scale-x-100" aria-hidden />
        </Button>

        <div className="relative flex w-full flex-col items-center px-8">
          <div className="mt-8 flex w-full flex-col items-center gap-6">
            <FramedIcon state={isClinicianPlan ? 'pro' : 'practice'} />

            <div className="flex w-full flex-col items-center gap-4 text-center">
              <DialogDrawerTitle
                className={cn(
                  typographyVariant({ variant: 'h2' }),
                  'text-balance text-center font-exposure text-text-primary'
                )}
              >
                <FormattedMessage {...trialUpgradeRequestCallbackModalMessages.title} />
              </DialogDrawerTitle>

              <DialogDrawerDescription asChild>
                <TypographyP className="text-center text-text-secondary">
                  <FormattedMessage {...descriptionMessage} />
                </TypographyP>
              </DialogDrawerDescription>
            </div>

            <Form {...form}>
              <form
                className="flex w-full flex-col gap-6"
                onSubmit={(e) => void form.handleSubmit(handleSubmit)(e)}
              >
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem className="gap-1.5">
                      <FormLabel className="text-start text-sm font-medium text-text-primary">
                        <FormattedMessage
                          {...trialUpgradeRequestCallbackModalMessages.messageLabel}
                        />
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={messagePlaceholder}
                          rows={4}
                          maxLength={TRIAL_UPGRADE_REQUEST_CALLBACK_MESSAGE_MAX_LENGTH}
                          className="min-h-[96px] resize-y"
                          disabled={isSubmitting}
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <div className="flex items-start justify-between gap-2">
                        <FormMessage className="min-w-0 flex-1" />
                        <TypographyCaption className="ms-auto shrink-0 text-text-tertiary">
                          {(field.value ?? '').length}/
                          {TRIAL_UPGRADE_REQUEST_CALLBACK_MESSAGE_MAX_LENGTH}
                        </TypographyCaption>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem className="gap-1.5">
                      <FormLabel className="text-start text-sm font-medium text-text-primary">
                        <FormattedMessage
                          {...trialUpgradeRequestCallbackModalMessages.contactNumberLabel}
                        />
                      </FormLabel>
                      <FormControl>
                        <PhoneInput
                          value={field.value}
                          onChange={(value) => field.onChange((value as string) ?? '')}
                          defaultCountry={defaultCountry as Country}
                          placeholder={contactNumberPlaceholder}
                          disabled={isSubmitting}
                          countrySelectPopoverClassName="z-[102]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col gap-2">
                  <Button
                    type="submit"
                    variant={isClinicianPlan ? 'pro' : 'pro_plus'}
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting || hasCallbackRequested}
                  >
                    {isSubmitting ? (
                      <Loader
                        className="me-3 shrink-0 text-text-primary-invert"
                        size={22}
                      />
                    ) : (
                      <FormattedMessage
                        {...trialUpgradeRequestCallbackModalMessages.send}
                      />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                    onClick={handleCancel}
                  >
                    <FormattedMessage
                      {...trialUpgradeRequestCallbackModalMessages.cancel}
                    />
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
